import { DraftItem } from "src/app/models/models";

/**
 * After a staging POST, decide what to persist locally without clobbering edits
 * that landed in IndexedDB while the request was in flight.
 *
 * @param syncedSnapshot - Draft payload that was sent to the API (pre-flight snapshot).
 * @param current - Latest IndexedDB record after the API returns (undefined if deleted).
 * @param ok - Whether the staging POST succeeded.
 * @returns Record to write, or `null` when the draft was deleted and must not be recreated.
 */
export function applySyncResultToLocalDraft(
	syncedSnapshot: Pick<DraftItem, "lastUpdatedDate">,
	current: DraftItem | undefined,
	ok: boolean,
): DraftItem | null {
	if (!current) {
		return null;
	}

	const snapshotTs = syncedSnapshot.lastUpdatedDate || "";
	const currentTs = current.lastUpdatedDate || "";
	const localChangedDuringSync = currentTs !== snapshotTs;

	if (localChangedDuringSync) {
		// Preserve newer local content; force another sync so the server gets it.
		return {
			...current,
			synced: false,
		};
	}

	// Local unchanged — only flip the sync flag; do not bump lastUpdatedDate.
	return {
		...current,
		synced: ok,
	};
}

/** Outcome of one staging POST for sign-off. `retry` means a newer edit or POST superseded this attempt. */
export type StagingSyncStatus = "synced" | "offline" | "http_error" | "retry";

export interface StagingSyncResult {
	status: StagingSyncStatus;
	error?: unknown;
}

/**
 * An older staging POST must not be written once a newer one has started.
 * Otherwise its timestamp mismatch clears `synced` after the newer POST already succeeded.
 */
export function shouldApplyStagingSyncResult(attemptGeneration: number, latestGeneration: number): boolean {
	return attemptGeneration === latestGeneration;
}

/**
 * Classify a staging attempt.
 * Status 0 is offline. Any other HTTP failure stays `http_error` so the UI does not ask the user to reconnect.
 * A superseded attempt or a local edit during a successful POST is `retry`, not a hard failure.
 */
export function resolveStagingSyncStatus(params: {
	/** Null when the POST returned 2xx. `0` is a network failure. */
	httpStatus: number | null;
	httpOk: boolean;
	attemptGeneration: number;
	latestGeneration: number;
	localUnchanged: boolean;
}): StagingSyncStatus {
	if (!shouldApplyStagingSyncResult(params.attemptGeneration, params.latestGeneration)) {
		return "retry";
	}
	if (!params.httpOk) {
		return params.httpStatus === 0 ? "offline" : "http_error";
	}
	if (!params.localUnchanged) {
		return "retry";
	}
	return "synced";
}

/**
 * How to reconcile in-memory draft after a queued IDB write / staging sync.
 * - `replace`: memory still matches this op → adopt persisted draft
 * - `keep_unsynced`: memory is ahead (newer epoch and/or timestamp) → keep memory, force unsynced
 */
export type MemoryDraftAdoptMode = "replace" | "keep_unsynced";

export function resolveMemoryDraftAdoptMode(params: {
	memoryEpoch: number;
	epochAtStart?: number;
	memLastUpdated?: string;
	nextLastUpdated?: string;
}): MemoryDraftAdoptMode {
	if (params.epochAtStart !== undefined && params.memoryEpoch !== params.epochAtStart) {
		return "keep_unsynced";
	}
	const memTs = new Date(params.memLastUpdated || 0).getTime();
	const nextTs = new Date(params.nextLastUpdated || 0).getTime();
	return memTs <= nextTs ? "replace" : "keep_unsynced";
}
